/**
 * ShareOptionRow.tsx — Fila reutilizable para menús de compartir.
 *
 * QUÉ HACE:
 *  - Renderiza una fila clickeable con un cuadro-icono a la izquierda, una
 *    etiqueta principal y un sub-texto explicativo. Se usa en los dropdowns
 *    de "Compartir ticket" (ventas) y "Compartir cierre" (cierres) para
 *    garantizar consistencia visual entre todos los menús de envío.
 *
 * QUÉ EXPORTA:
 *  - ShareOptionRow (named)
 *  - SharePalette (type: paleta de color predefinida)
 *
 * QUÉ NO HACE:
 *  - No conoce de receipts, cierres ni de captura DOM. Es UI pura.
 *  - No persiste estado.
 *
 * REGLAS:
 *  - Cualquier menú de compartir nuevo DEBE usar este componente para no
 *    fragmentar el lenguaje visual.
 *  - En viewport < sm, padding y texto se reducen; desde sm se mantiene el
 *    tamaño cómodo para desktop.
 *  - Las paletas son fijas (verde/whatsapp, esmeralda/share, azul/descarga,
 *    indigo/imagen, ámbar/email). Para una nueva acción, usar la paleta más
 *    cercana al canal en vez de inventar colores nuevos.
 */

import type { ReactNode } from "react";

export type SharePalette =
  | "whatsapp"
  | "image"
  | "share"
  | "download"
  | "email"
  | "neutral";

const PALETTES: Record<
  SharePalette,
  { text: string; hover: string; iconBg: string }
> = {
  whatsapp: {
    text: "text-green-700 dark:text-green-300",
    hover: "hover:bg-green-50 dark:hover:bg-green-900/30",
    iconBg: "bg-green-100 dark:bg-green-900/40",
  },
  image: {
    text: "text-indigo-700 dark:text-indigo-300",
    hover: "hover:bg-indigo-50 dark:hover:bg-indigo-900/30",
    iconBg: "bg-indigo-100 dark:bg-indigo-900/40",
  },
  share: {
    text: "text-emerald-700 dark:text-emerald-300",
    hover: "hover:bg-emerald-50 dark:hover:bg-emerald-900/30",
    iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
  },
  download: {
    text: "text-blue-700 dark:text-blue-300",
    hover: "hover:bg-blue-50 dark:hover:bg-blue-900/30",
    iconBg: "bg-blue-100 dark:bg-blue-900/40",
  },
  email: {
    text: "text-amber-700 dark:text-amber-300",
    hover: "hover:bg-amber-50 dark:hover:bg-amber-900/30",
    iconBg: "bg-amber-100 dark:bg-amber-900/40",
  },
  neutral: {
    text: "text-gray-700 dark:text-slate-200",
    hover: "hover:bg-gray-50 dark:hover:bg-slate-700/40",
    iconBg: "bg-gray-100 dark:bg-slate-700",
  },
};

interface ShareOptionRowProps {
  icon: ReactNode;
  label: string;
  description?: string;
  palette?: SharePalette;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export const ShareOptionRow = ({
  icon,
  label,
  description,
  palette = "neutral",
  onClick,
  disabled = false,
  className = "",
}: ShareOptionRowProps) => {
  const p = PALETTES[palette];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 sm:gap-3 w-full px-2 py-2 sm:px-3 sm:py-3 rounded-md sm:rounded-lg text-[11px] sm:text-sm font-medium transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed ${p.text} ${p.hover} ${className}`}
    >
      <span
        className={`flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg shrink-0 ${p.iconBg}`}
      >
        {icon}
      </span>
      <span className="flex flex-col min-w-0">
        <span className="leading-tight truncate">{label}</span>
        {description && (
          <span className="text-[9px] sm:text-[11px] text-gray-400 dark:text-slate-500 font-normal leading-tight truncate">
            {description}
          </span>
        )}
      </span>
    </button>
  );
};
